import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import ThemeToggle from "shared/ui/ThemeToggle";
import { useTheme } from "app/providers/ThemeProvider";
import "./Navbar.css";

const logoDark = "/images/LogoVectorizado.dark.png";
const logoLight = "/images/LogoVectorizado.light.png";

const SCROLL_THRESHOLD = 40;
const DROPDOWN_CLOSE_DELAY = 150;
const HAMBURGER_BREAKPOINT = 900;

const ROUTES = {
  constructionHome: "/constructora",
  portal: "/portal",
  nosotros: "/constructora/nosotros",
  contacto: "/constructora/contacto",
  proyectos: "/constructora/proyectos",
  servicios: "/constructora/servicios",
  edificacion: "/constructora/servicios/edificacion",
  infraestructura: "/constructora/servicios/infraestructura",
  especialidades: "/constructora/servicios/especialidades",
  ingenieria: "/constructora/servicios/ingenieria",
};

const NAV_ITEMS = [
  { label: "Inicio", dropdown: null, direct: { type: "route", to: ROUTES.portal }, icon: "🏠" },
  { label: "Constructora", dropdown: null, direct: { type: "route", to: ROUTES.constructionHome }, icon: "🏗️" },
  { label: "Nosotros", dropdown: null, direct: { type: "route", to: ROUTES.nosotros } },
  {
    label: "Servicios",
    dropdown: [
      { label: "Edificación", type: "route", to: ROUTES.edificacion },
      { label: "Infraestructura", type: "route", to: ROUTES.infraestructura },
      { label: "Especialidades", type: "route", to: ROUTES.especialidades },
      { label: "Ingeniería & Arquitectura", type: "route", to: ROUTES.ingenieria },
      { label: "Ver todos los servicios", type: "route", to: ROUTES.servicios },
    ],
    icon: "🛠️",
  },
  { label: "Contáctanos", dropdown: null, direct: { type: "route", to: ROUTES.contacto }, icon: "📞" },
];

function normalizePath(p) {
  const s = String(p || "/").replace(/\/+$/, "");
  return (s || "/").toLowerCase();
}

function isPathMatch(pathname, target) {
  if (!target) return false;
  const p = normalizePath(pathname);
  const t = normalizePath(target);
  if (p === t) return true;
  return p.startsWith(`${t}/`);
}

function isExactPath(pathname, target) {
  return normalizePath(pathname) === normalizePath(target);
}

function getScrollHost() {
  const container = document.querySelector(".App");
  return { container, host: container || window };
}

function scrollToTop(behavior = "smooth") {
  const { container } = getScrollHost();
  if (container) container.scrollTo({ top: 0, behavior });
  else window.scrollTo({ top: 0, behavior });
}

export default function Navbar() {
  const { darkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const navRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);

  const [scrolled, setScrolled] = useState(() => {
    if (typeof window === "undefined") return false;
    const { container } = getScrollHost();
    const y = container ? container.scrollTop : window.scrollY;
    return y > SCROLL_THRESHOLD;
  });

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const logoSrc = useMemo(() => {
    if (!scrolled) return logoLight;
    return darkMode ? logoLight : logoDark;
  }, [scrolled, darkMode]);

  const activeTopLabel = useMemo(() => {
    const p = location.pathname || "/";
    if (isPathMatch(p, ROUTES.portal)) return "Inicio";
    if (isPathMatch(p, ROUTES.nosotros)) return "Nosotros";
    if (isPathMatch(p, ROUTES.servicios)) return "Servicios";
    if (isPathMatch(p, ROUTES.contacto)) return "Contáctanos";
    if (isPathMatch(p, ROUTES.constructionHome)) return "Constructora";
    return null;
  }, [location.pathname]);

  const resetMenus = useCallback(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
    setMobileDropdown(null);
  }, []);

  const closeMenu = useCallback(() => {
    resetMenus();
  }, [resetMenus]);

  const toggleMenu = useCallback(() => {
    setMenuOpen((v) => !v);
  }, []);

  const toggleMobileDropdown = useCallback((label) => {
    setMobileDropdown((prev) => (prev === label ? null : label));
  }, []);

  const isTopItemActiveByRoute = useCallback(
    (item) => activeTopLabel === item.label,
    [activeTopLabel]
  );

  const handleMouseEnter = useCallback((label) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(label);
  }, []);

  const handleMouseLeave = useCallback(() => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, DROPDOWN_CLOSE_DELAY);
  }, []);

  const goToContact = useCallback(() => {
    closeMenu();
    navigate(ROUTES.contacto, { replace: false });
  }, [closeMenu, navigate]);

  const renderLink = useCallback((item, extraProps = {}) => {
    return (
      <RouterLink to={item.to} {...extraProps}>
        {item.label}
      </RouterLink>
    );
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") resetMenus();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
      if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    };
  }, [resetMenus]);

  useEffect(() => {
    const { container, host } = getScrollHost();
    let raf = 0;

    const readY = () => (container ? container.scrollTop : window.scrollY);

    const handleScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const y = readY();
        const next = y > SCROLL_THRESHOLD;
        setScrolled((prev) => (prev === next ? prev : next));
      });
    };

    host.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      host.removeEventListener("scroll", handleScroll);
    };
  }, [location.pathname]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > HAMBURGER_BREAKPOINT && menuOpen) setMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [menuOpen]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [menuOpen]);

  useEffect(() => {
    resetMenus();
  }, [location.pathname, resetMenus]);

  return (
    <>
      <div className={`nav-border-fix ${scrolled ? "scrolled" : ""}`} />

      <nav
        ref={navRef}
        className={`n-wrapper ${darkMode ? "is-dark" : "is-light"} ${scrolled ? "scrolled" : "top"} ${menuOpen ? "has-open-menu" : ""}`}
        role="navigation"
        aria-label="Main Navigation"
        style={{ height: "var(--navbar-height)" }}
        onMouseLeave={handleMouseLeave}
      >
        <div className="n-left">
          <RouterLink
            to={ROUTES.constructionHome}
            aria-label="Inicio Constructora"
            className="logo-link"
            onClick={(e) => {
              closeMenu();
              setActiveDropdown(null);
              if (isExactPath(location.pathname, ROUTES.constructionHome)) {
                e.preventDefault();
                scrollToTop("smooth");
              }
            }}
          >
            <img src={logoSrc} alt="GRUPO ARCA C&N" className="logo-img" loading="eager" decoding="sync" />
          </RouterLink>

          <div className="toggle-container" aria-label="Theme toggle">
            <ThemeToggle />
          </div>
        </div>

        <div className="n-center">
          <div className="n-list" aria-label="Primary">
            <ul>
              {NAV_ITEMS.map((it) => {
                const hasDropdown = Array.isArray(it.dropdown) && it.dropdown.length > 0;
                const isActive = activeDropdown === it.label;
                const isCurrentRoute = isTopItemActiveByRoute(it);

                return (
                  <li
                    key={it.label}
                    className={`nav-item ${hasDropdown ? "has-dropdown" : ""} ${isActive ? "dropdown-open" : ""} ${isCurrentRoute ? "is-current" : ""}`}
                    onMouseEnter={() => hasDropdown && handleMouseEnter(it.label)}
                    onMouseLeave={() => hasDropdown && handleMouseLeave()}
                  >
                    {hasDropdown ? (
                      <>
                        <button
                          type="button"
                          className="nav-link nav-link-btn"
                          aria-haspopup="true"
                          aria-expanded={isActive}
                          aria-current={isCurrentRoute ? "page" : undefined}
                          onClick={() => setActiveDropdown(isActive ? null : it.label)}
                        >
                          {it.label} <span className="caret" aria-hidden="true" />
                        </button>

                        <div
                          className={`dropdown ${isActive ? "active" : ""}`}
                          role="menu"
                          onMouseEnter={() => handleMouseEnter(it.label)}
                          onMouseLeave={handleMouseLeave}
                        >
                          {it.dropdown.map((d) => {
                            const isDropdownCurrent = isPathMatch(location.pathname, d.to);

                            return (
                              <div key={`${it.label}-${d.label}`} className="dropdown-item">
                                {renderLink(
                                  { ...d, label: d.label },
                                  {
                                    className: `dropdown-link ${isDropdownCurrent ? "is-current" : ""}`,
                                    "aria-current": isDropdownCurrent ? "page" : undefined,
                                    onClick: () => {
                                      closeMenu();
                                      setActiveDropdown(null);
                                    },
                                  }
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      renderLink(
                        { ...it.direct, label: it.label },
                        {
                          className: "nav-link",
                          "aria-current": isCurrentRoute ? "page" : undefined,
                          onClick: (e) => {
                            closeMenu();
                            setActiveDropdown(null);

                            if (it.label === "Constructora" && isExactPath(location.pathname, ROUTES.constructionHome)) {
                              e.preventDefault();
                              scrollToTop("smooth");
                            }
                          },
                        }
                      )
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="n-right">
          <button type="button" className="button n-button" aria-label="Cotizar" onClick={goToContact}>
            Cotizar
          </button>

          <button
            type="button"
            className={`hamburger ${menuOpen ? "open" : ""}`}
            aria-label={menuOpen ? "Cerrar menú móvil" : "Abrir menú móvil"}
            aria-expanded={menuOpen}
            onClick={toggleMenu}
          >
            <svg className="hamburger-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path className="line line1" d="M5 7H19" />
              <path className="line line2" d="M5 12H19" />
              <path className="line line3" d="M5 17H19" />
            </svg>
          </button>
        </div>
      </nav>

      <div
        className={`mobile-menu ${menuOpen ? "open" : ""} ${darkMode ? "is-dark" : "is-light"}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!menuOpen}
        onClick={closeMenu}
      >
        <div className="mobile-menu-inner" onClick={(e) => e.stopPropagation()}>
          <div className="mobile-header">
            <RouterLink
              to={ROUTES.constructionHome}
              className="mobile-logo-container"
              onClick={(e) => {
                closeMenu();
                if (isExactPath(location.pathname, ROUTES.constructionHome)) {
                  e.preventDefault();
                  scrollToTop("smooth");
                }
              }}
              aria-label="Inicio Constructora"
            >
              <img src={logoSrc} alt="GRUPO ARCA C&N" className="mobile-logo" loading="eager" decoding="sync" />
              <span className="mobile-company-name">GRUPO ARCA</span>
            </RouterLink>

            <button type="button" className="mobile-close-btn" onClick={closeMenu} aria-label="Cerrar menú">
              <svg className="close-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6L18 18" />
                <path d="M18 6L6 18" />
              </svg>
            </button>
          </div>

          <ul className="mobile-list">
            {NAV_ITEMS.map((it) => {
              const hasDropdown = Array.isArray(it.dropdown) && it.dropdown.length > 0;
              const isMobileOpen = mobileDropdown === it.label;
              const isCurrentRoute = isTopItemActiveByRoute(it);

              if (hasDropdown) {
                return (
                  <li key={`m-${it.label}`} className={`mobile-item ${isCurrentRoute ? "is-current" : ""}`}>
                    <div className="mobile-item-main" onClick={() => toggleMobileDropdown(it.label)}>
                      <div className="mobile-item-title">
                        <span className="mobile-item-icon" aria-hidden="true">
                          {it.icon}
                        </span>
                        {it.label}
                      </div>
                      <span className={`mobile-caret ${isMobileOpen ? "open" : ""}`} aria-hidden="true" />
                    </div>

                    <div className={`mobile-sub ${isMobileOpen ? "open" : ""}`}>
                      {it.dropdown.map((d) => {
                        const isDropdownCurrent = isPathMatch(location.pathname, d.to);

                        return (
                          <div key={`m-${it.label}-${d.label}`} className="mobile-sub-item">
                            {renderLink(
                              { ...d, label: d.label },
                              {
                                className: `mobile-link ${isDropdownCurrent ? "is-current" : ""}`,
                                "aria-current": isDropdownCurrent ? "page" : undefined,
                                onClick: closeMenu,
                              }
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </li>
                );
              }

              return (
                <li key={`m-${it.label}`} className={`mobile-item ${isCurrentRoute ? "is-current" : ""}`}>
                  <RouterLink
                    to={it.direct.to}
                    className={`mobile-direct-link ${isCurrentRoute ? "is-current" : ""}`}
                    aria-current={isCurrentRoute ? "page" : undefined}
                    onClick={(e) => {
                      closeMenu();
                      if (it.label === "Constructora" && isExactPath(location.pathname, ROUTES.constructionHome)) {
                        e.preventDefault();
                        scrollToTop("smooth");
                      }
                    }}
                  >
                    {it.label}
                  </RouterLink>
                </li>
              );
            })}
          </ul>

          <div className="mobile-footer">
            <div className="mobile-toggle-section">
              <div className="mobile-toggle-header">
                <div className="mobile-toggle-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path d="M12 3V21M21 12H3" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="mobile-toggle-info">
                  <div className="mobile-toggle-title">Apariencia</div>
                  <div className="mobile-toggle-subtitle">Modo claro u oscuro</div>
                </div>
              </div>
              <div className="mobile-toggle-action">
                <ThemeToggle />
              </div>
            </div>

            <RouterLink to={ROUTES.contacto} className="mobile-cta" onClick={closeMenu} aria-label="Solicitar Cotización">
              Solicitar Cotización
            </RouterLink>

            <RouterLink to={ROUTES.servicios} className="mobile-quick" onClick={closeMenu} aria-label="Ver servicios">
              Ver servicios
            </RouterLink>
          </div>
        </div>
      </div>
    </>
  );
}
